/**
 * UpSurv 2.0 — the deterministic calculation engine (specification §5).
 *
 * Every AUTO quantity in the specification is computed here and nowhere else.
 * Spec §5 is explicit that this is code and never an LLM, and brief §3 makes it
 * "pure functions, unit-tested, grep-able": each formula is a standalone export
 * taking primitives and returning a `DerivedLine`, and `computeDerived(doc)`
 * walks a whole survey emitting those lines at the right entity scope.
 *
 * A `DerivedLine` is two things at once. On the phone it is the
 * "⚙ calculated for you" chip the surveyor reads — which is why every line
 * carries a plain-language `basis` written the way the mockup writes it
 * ("6 cars ÷ 3, rounded up", "one run to each car"). In the office it is an
 * estimate line: quantity × unit price, keyed on `questionId`. So an off-by-one
 * in a ceil() here is a mispriced job.
 *
 * ── Three rules the whole file obeys ────────────────────────────────────────
 *
 *  1. **Gates resolve through `conditionScope`.** A `showWhen` condition is
 *     evaluated at the level of the REFERENCED question, not the asking scope.
 *     L4 is a per-group question gated on B8, a building answer; building
 *     "room:r1|grp:gA|B8" yields a key that can never exist and would silently
 *     close the gate. Nothing in this file assembles a condition's answer key
 *     by hand.
 *
 *  2. **Fully-derived questions are read from STRUCTURE, never from answers.**
 *     B14/B17 come from `CarRef.onAts`, L1 from `Group.landings`, the room unit
 *     count from `MachineRoom.cars`. Those ids never appear in
 *     `SurveyDocument.answers` (catalog.ts `DERIVED_ONLY_QUESTION_IDS`), so a
 *     stored value for one of them is stale data and is ignored outright.
 *     The other AUTO questions — E6, E8, L8 (a surveyor Y/N) and E7c, H3 (a
 *     surveyor measurement) — DO carry an answer, and it is the surveyor's
 *     input that is read, never a stored quantity.
 *
 *  3. **A line is emitted only when its gate is open, its inputs are present,
 *     and its quantity is positive.** A zero-quantity line is never money and
 *     would be noise in the estimate; the raw formulas below still return the
 *     zero so the form and the validation engine can show it.
 *
 * Nothing here reads a clock, a random source, or a Node API: two calls on
 * equal documents produce byte-identical output, which is what lets the
 * St. Luke's fixture round-trip (brief §6.4).
 */

import { byId, conditionScope } from './catalog';
import {
  answerKey,
  formSetFor,
  type AnswerValue,
  type DerivedLine,
  type Group,
  type MachineRoom,
  type QuestionCondition,
  type Scope,
  type SurveyDocument,
} from './types';

/* ───────────────────────────── tunable constants ────────────────────────── */

/** E6: one 2019 emergency-communications disconnect per this many cars (Q14). */
export const CARS_PER_EMERGENCY_COMMS_DISCONNECT = 3;

/** E7c and H3 are both priced in 25-ft units (spec §3.3, §3.4). */
export const CONDUIT_UNIT_FEET = 25;

/**
 * H3: the standard line covers runs up to and including 100 ft. Chip calls it
 * the refrigerant line (D-053, 2026-10-27 — "Yes" to the same 100 ft + 25-ft
 * units); the `CONDENSATE_*` names are code and keep their spelling.
 */
export const CONDENSATE_STANDARD_FEET = 100;

/**
 * H3 produces TWO catalog lines — a standard run plus per-25-ft overage — and
 * the specification names only one id for them. The overage therefore gets an
 * explicit sub-line id rather than an invented question id: a dot can never
 * collide with a §3 id (they are alphanumeric), it sorts next to `H3`, and it
 * is greppable. Its pricing and UOM are still taken from catalog entry `H3`.
 */
export const CONDENSATE_OVERAGE_ID = 'H3.overage';

/** The standard condensate line is a run, not a 25-ft unit — see above. */
const CONDENSATE_STANDARD_UOM = 'run';

/* ─────────────────────────────── small helpers ──────────────────────────── */

/**
 * `ceil(value ÷ per)`, guarded against floating-point dust.
 *
 * A measured distance arrives as a JS number, so a value that is mathematically
 * an exact multiple can land a hair above it and round up a whole unit — 25 ft
 * of conduit billed as two units. Anything within 1e-9 of an integer is treated
 * as that integer; everything else rounds up as the specification says.
 */
function ceilUnits(value: number, per: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  const raw = value / per;
  const nearest = Math.round(raw);
  return Math.abs(raw - nearest) < 1e-9 ? nearest : Math.ceil(raw);
}

/**
 * A count of discrete, priceable things: floors punched, fixtures hung.
 *
 * These arrive from `number` answers, and schema.ts constrains a number answer
 * only to `finite()` — a negative or fractional value is representable in a
 * perfectly valid document, and no §4 rule rejects one. Pricing "21.5 floor
 * punches" or "−2 fixtures" is a fabricated number on a customer estimate, and
 * a negative quantity additionally violates `DerivedLineSchema` (nonnegative).
 * So every such count is normalised to a whole non-negative one BEFORE it
 * reaches either a quantity or a basis string. Rounding UP, not down: you
 * cannot punch part of a floor, and the partial one still has to be crossed.
 */
function wholeCount(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 0;
  return Math.ceil(value);
}

/**
 * A measured distance, rendered for a human.
 *
 * `130.3 - 100` is `30.30000000000001` in binary floating point, and that is
 * what the surveyor's "⚙ calculated for you" chip printed — fourteen digits of
 * float dust in the one string that is supposed to make the math trustworthy.
 * Round to a tenth of a foot, finer than anyone paces a conduit run, and drop a
 * trailing ".0" so whole numbers read as whole numbers.
 */
function formatFeet(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** "1 car" / "6 cars" — the basis text is read by a person, so it agrees. */
function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * Build a derived line, taking pricing and UOM from the catalog so the two
 * cannot drift. `catalogId` differs from `questionId` only for the condensate
 * overage sub-line, which prices off `H3`.
 */
function derivedLine(args: {
  questionId: string;
  catalogId?: string;
  scope: Scope;
  quantity: number;
  basis: string;
  uom?: string;
}): DerivedLine {
  const catalogId = args.catalogId ?? args.questionId;
  const def = byId(catalogId);
  if (!def) {
    // Programmer error, not survey data: a derived line is keyed on a
    // specification id that the catalog no longer carries. Fail loudly — a
    // silent fallback would ship a line the estimate engine cannot price.
    throw new Error(`calc: no catalog entry for question "${catalogId}"`);
  }
  return {
    questionId: args.questionId,
    scope: args.scope,
    quantity: args.quantity,
    uom: args.uom ?? def.uom ?? 'ea',
    basis: args.basis,
    pricing: def.pricing,
  };
}

function findRoom(doc: SurveyDocument, roomId: string | undefined): MachineRoom | undefined {
  if (!roomId) return undefined;
  return doc.rooms.find((room) => room.id === roomId);
}

/* ───────────────────────── answers & gate resolution ────────────────────── */

function answerAt(doc: SurveyDocument, questionId: string, scope: Scope): AnswerValue | undefined {
  return doc.answers[answerKey(questionId, scope)];
}

function boolAnswer(doc: SurveyDocument, questionId: string, scope: Scope): boolean | undefined {
  const answer = answerAt(doc, questionId, scope);
  return answer?.kind === 'bool' ? answer.value : undefined;
}

function numberAnswer(doc: SurveyDocument, questionId: string, scope: Scope): number | undefined {
  const answer = answerAt(doc, questionId, scope);
  return answer?.kind === 'number' ? answer.value : undefined;
}

/** The count of a Yes/No + "how many" answer: 0 on a No, undefined when unanswered. */
function flagCountAnswer(doc: SurveyDocument, questionId: string, scope: Scope): number | undefined {
  const answer = answerAt(doc, questionId, scope);
  if (answer?.kind !== 'flagCount') return undefined;
  return answer.value.flag ? answer.value.count : 0;
}

/** The single-dimension measurement behind E7c and H3. */
function feetAnswer(doc: SurveyDocument, questionId: string, scope: Scope): number | undefined {
  const answer = answerAt(doc, questionId, scope);
  return answer?.kind === 'measure' ? answer.value.feet : undefined;
}

/** The scalar an `equals` condition can be compared against. */
function comparable(answer: AnswerValue): boolean | string | number | undefined {
  switch (answer.kind) {
    case 'text':
    case 'select':
    case 'date':
    case 'tristate':
    case 'number':
    case 'bool':
      return answer.value;
    default:
      // multiselect / contact / measure / ranges / flagCount are never the
      // subject of an `equals` gate; flagCount uses `flagIs` instead.
      return undefined;
  }
}

function conditionHolds(doc: SurveyDocument, condition: QuestionCondition, scope: Scope): boolean {
  if (condition.structural) {
    const room = findRoom(doc, scope.roomId);
    if (!room) return false;
    const set = formSetFor(room.type);
    return condition.structural === 'roomTypeIsTraction' ? set === 'traction' : set === 'hydro';
  }

  // A gate whose own question is not asked is a CLOSED gate: a stale answer left
  // behind when a parent gate was changed must not resurrect the branch under it
  // and put a priced line back into the estimate. See the longer note in
  // validation.ts conditionHolds — the catalog chain that needs this is
  // B8 → L4 → L4a. `scope` arrives already narrowed to the referenced question's
  // level, which is the scope its own gates must be read in.
  if (byId(condition.questionId) && !isQuestionVisible(doc, condition.questionId, scope)) {
    return false;
  }

  const answer = answerAt(doc, condition.questionId, scope);
  // An unanswered gate is a CLOSED gate, including for `equals: false`: "not yet
  // told" is not the same as "told no", and guessing would emit a priced line.
  if (!answer) return false;

  if (condition.flagIs !== undefined) {
    return answer.kind === 'flagCount' && answer.value.flag === condition.flagIs;
  }
  if (condition.equals !== undefined) {
    return comparable(answer) === condition.equals;
  }
  return true;
}

/**
 * Whether a question is asked at all in this scope: its form set matches the
 * room's, and every `showWhen` condition holds — each resolved at the level of
 * the question it references, via `conditionScope`.
 */
export function isQuestionVisible(
  doc: SurveyDocument,
  questionId: string,
  scope: Scope,
): boolean {
  const def = byId(questionId);
  if (!def) return false;

  const room = findRoom(doc, scope.roomId);
  if (def.formSets && room && !def.formSets.includes(formSetFor(room.type))) return false;
  if (!def.showWhen) return true;

  return def.showWhen.every((condition) =>
    conditionHolds(doc, condition, conditionScope(condition, scope)),
  );
}

/* ══════════════════ unit counts — Q4 and the B11 cross-check ═════════════ */

/** Room unit count is derived from the car list and never typed (spec Q4). */
export function roomUnitCount(room: MachineRoom): number {
  return room.cars.length;
}

/** The M3 "⚙ N units — counted for you" chip from the mockup. */
export function roomUnitCountLine(room: MachineRoom): DerivedLine {
  return derivedLine({
    questionId: 'M3',
    scope: { roomId: room.id },
    quantity: roomUnitCount(room),
    basis: 'counted from the car list',
  });
}

/** Sum of the car lists across every machine room survey (spec §4 rule 1). */
export function buildingUnitCount(doc: SurveyDocument): number {
  return doc.rooms.reduce((total, room) => total + roomUnitCount(room), 0);
}

export interface UnitCountCrossCheck {
  /** B11 as the surveyor typed it, or undefined while it is unanswered. */
  buildingTotal: number | undefined;
  /** Sum of the per-room car lists. */
  roomTotal: number;
  perRoom: { roomId: string; roomName: string; units: number }[];
  /** True only when B11 is present AND equals the derived total. */
  matches: boolean;
}

/**
 * Specification §4 rule 1, computed but not judged: the validation engine turns
 * a mismatch into a `ValidationIssue`. Calc supplies the arithmetic so both the
 * building screen's live cross-check and that rule read the same numbers.
 */
export function unitCountCrossCheck(doc: SurveyDocument): UnitCountCrossCheck {
  const buildingTotal = numberAnswer(doc, 'B11', {});
  const roomTotal = buildingUnitCount(doc);
  return {
    buildingTotal,
    roomTotal,
    perRoom: doc.rooms.map((room) => ({
      roomId: room.id,
      roomName: room.name,
      units: roomUnitCount(room),
    })),
    matches: buildingTotal !== undefined && buildingTotal === roomTotal,
  };
}

/* ════════════════ B14 / B17 — the ATS chain (D-003, approved) ════════════ */

export interface AtsRollup {
  /** B14 — cars tagged `onAts` across every machine room. */
  cars: number;
  /** B17 — machine rooms containing at least one tagged car. */
  rooms: number;
}

/**
 * D-003: ATS membership is a per-car fact, so both ATS numbers derive from the
 * car tags and neither is ever typed. A room counts once however many of its
 * cars are tagged, because B17 is priced per machine room (Q26).
 */
export function atsRollup(rooms: readonly MachineRoom[]): AtsRollup {
  let cars = 0;
  let withAts = 0;
  for (const room of rooms) {
    const tagged = room.cars.filter((car) => car.onAts).length;
    cars += tagged;
    if (tagged > 0) withAts += 1;
  }
  return { cars, rooms: withAts };
}

export function atsCarCountLine(rollup: AtsRollup): DerivedLine {
  return derivedLine({
    questionId: 'B14',
    scope: {},
    quantity: rollup.cars,
    basis: 'counted from the cars you tagged on ATS',
  });
}

/** Spec §3.1: the line reads "ATS – X cars in Y Machine Rooms." */
export function atsRoomCountLine(rollup: AtsRollup): DerivedLine {
  return derivedLine({
    questionId: 'B17',
    scope: {},
    quantity: rollup.rooms,
    basis: `ATS – ${plural(rollup.cars, 'car')} in ${plural(rollup.rooms, 'Machine Room')}.`,
  });
}

/* ═════════════ F1 — FLS back boxes, per landing, building-wide ════════════ */

/**
 * Every landing served by every group in the building (spec §3.11, Amendment 5).
 *
 * The riser assumption is the same one L8 is priced on — **one riser per group**
 * — so one FLS back box lands at each landing that riser passes, and the
 * building's total is the sum of the group landing counts across every machine
 * room.
 *
 * BUILDING-SCOPED, which makes it the odd one out in this file: every other
 * derived line is scoped to a room or a group, and this one reaches through
 * `doc.rooms[].groups[]` to add them all up. It takes the rooms rather than the
 * document so it stays a pure function of the structure it actually reads.
 *
 * `wholeCount` for the same reason `emitGroupLines` uses it: a landing count is
 * schema-pinned to a positive integer, and a per-landing price must never be
 * multiplied by half a landing.
 */
export function flsBackBoxCount(rooms: readonly MachineRoom[]): number {
  let boxes = 0;
  for (const room of rooms) {
    for (const group of room.groups) boxes += wholeCount(group.landings);
  }
  return boxes;
}

/** The number of groups those landings are spread across — for the chip's text. */
function groupCount(rooms: readonly MachineRoom[]): number {
  return rooms.reduce((total, room) => total + room.groups.length, 0);
}

export function flsBackBoxLine(rooms: readonly MachineRoom[]): DerivedLine {
  const landings = flsBackBoxCount(rooms);
  const groups = groupCount(rooms);
  return derivedLine({
    questionId: 'F1',
    scope: {},
    quantity: landings,
    basis:
      `one at every landing — ${plural(landings, 'landing')} across ` +
      `${plural(groups, 'group')} in this building`,
  });
}

/* ═══════════════════════════ electrical (§3.3) ═══════════════════════════ */

/** E6 — one 2019 emergency comms disconnect per three cars, rounded up (Q14). */
export function emergencyCommsUnits(carCount: number): number {
  return ceilUnits(carCount, CARS_PER_EMERGENCY_COMMS_DISCONNECT);
}

export function emergencyCommsDisconnectLine(carCount: number, scope: Scope): DerivedLine {
  return derivedLine({
    questionId: 'E6',
    scope,
    quantity: emergencyCommsUnits(carCount),
    basis: `${plural(carCount, 'car')} ÷ ${CARS_PER_EMERGENCY_COMMS_DISCONNECT}, rounded up`,
  });
}

/** E6a — CAT6, one run to each car, auto-added when E6 = Y (Q14). */
export function cat6Line(carCount: number, scope: Scope): DerivedLine {
  return derivedLine({
    questionId: 'E6a',
    scope,
    quantity: carCount,
    basis: 'one run to each car',
  });
}

/** E8 — convenience GFCI + non-GFCI outlets, one per car. */
export function outletLine(carCount: number, scope: Scope): DerivedLine {
  return derivedLine({
    questionId: 'E8',
    scope,
    quantity: carCount,
    basis: 'one per car in this room',
  });
}

/** E4b — shunting the room externally is one line for the whole room (Q13). */
export function externalShuntLine(scope: Scope): DerivedLine {
  return derivedLine({
    questionId: 'E4b',
    scope,
    quantity: 1,
    basis: 'one shunt for the whole room',
  });
}

/** What E4c needs from the room's disconnect answers. */
export interface DisconnectSplit {
  cars: number;
  /** E1a — new disconnects; undefined when unanswered. */
  replaced: number | undefined;
  /** E1b — disconnects kept and refurbished; undefined when unanswered. */
  refurbished: number | undefined;
  /** E4c1 — do the refurbished ones already have shunts? undefined when unanswered. */
  refurbishedHaveShunts: boolean | undefined;
}

/**
 * E4c — shunts inside the disconnects: every disconnect has to carry a shunt,
 * but a refurbished one only survives if it already has one (§4 rule 5). So the
 * NEW shunted disconnects are the ones being replaced (E1a), plus any marked
 * refurbish whose shunt the surveyor said is missing (E4c1 = No) — the form
 * flips those to replace anyway, so this is E1a after the flip. Refurbished
 * disconnects that already have shunts are not new and are not counted here
 * (HCA Tallahassee, 10/30/26: 7 refurbished, already shunted, were priced as 7
 * new). Disconnects neither E1a nor E1b accounts for (an answer left blank, or
 * a split short of the cars) count as new, as the car count did before: V2
 * puts the split on the gap list, and an unknown is never a silent $0. Never
 * more than the room's car count, even on a split that does not add up (V2).
 */
export function shuntedDisconnectLine(split: DisconnectSplit, scope: Scope): DerivedLine {
  const replaced = split.replaced ?? 0;
  const refurbished = split.refurbished ?? 0;
  const noShunt = split.refurbishedHaveShunts === false ? refurbished : 0;
  const unaccounted = Math.max(0, split.cars - replaced - refurbished);
  const quantity = Math.min(split.cars, replaced + noShunt + unaccounted);
  const parts = [`${replaced} being replaced`];
  if (noShunt > 0) parts.push(`${noShunt} marked refurbish with no shunt, so replaced`);
  if (unaccounted > 0) parts.push(`${unaccounted} not accounted for, counted as new`);
  let basis = `${parts.join(' + ')} — each new disconnect carries a shunt`;
  if (refurbished > 0 && split.refurbishedHaveShunts === true) {
    basis += `; ${refurbished} refurbished already have shunts`;
  }
  return derivedLine({ questionId: 'E4c', scope, quantity, basis });
}

/**
 * E7b — the vertical conduit leg: one punch per floor down to grade (Q16).
 *
 * The floor count is a `number` answer, so it is normalised through
 * `wholeCount` before it becomes either a quantity or a chip: half a floor
 * cannot be punched, and "21.5 floor" is not a line an estimator can price.
 */
export function conduitVerticalLine(floors: number, scope: Scope): DerivedLine {
  const punches = wholeCount(floors);
  return derivedLine({
    questionId: 'E7b',
    scope,
    quantity: punches,
    basis: `${plural(punches, 'floor')} from this machine room to the ground`,
  });
}

/** E7c — the horizontal conduit leg, priced in 25-ft units. 60 ft = 3 (Q16). */
export function conduitUnits(feet: number): number {
  return ceilUnits(feet, CONDUIT_UNIT_FEET);
}

export function conduitHorizontalLine(feet: number, scope: Scope): DerivedLine {
  return derivedLine({
    questionId: 'E7c',
    scope,
    quantity: conduitUnits(feet),
    basis: `${formatFeet(feet)} ft ÷ ${CONDUIT_UNIT_FEET}, rounded up`,
  });
}

/**
 * E9d — the guarded LED fixtures the room needs, as the surveyor counted them.
 *
 * Until D-045 this was "existing (E9a) + additional (E9c)": E9a asked how many
 * fixtures were in the room NOW and every one was replaced. Chip's 2026-09-11
 * wording asks E9a for the fixtures NEEDED, so the line is that number; D-046
 * then deleted E9b/E9c outright. The one input still goes through
 * `wholeCount`: a negative or fractional answer is representable, and this
 * line once priced below zero off one (see the regression block in
 * calc.test.ts).
 */
export function ledFixtureLine(needed: number, scope: Scope): DerivedLine {
  const count = wholeCount(needed);
  return derivedLine({
    questionId: 'E9d',
    scope,
    quantity: count,
    basis: `${count} needed`,
  });
}

/* ══════════════════════════════ HVAC (§3.4) ══════════════════════════════ */

export interface CondensateUnits {
  /** 1 for any measured run; 0 when there is no run to price (see below). */
  standard: number;
  /** Additional 25-ft units past 100 ft. 130 ft ⇒ 2. */
  overage: number;
}

/**
 * H3 — the standard price covers a run of 100 ft or less; past that it is one
 * more 25-ft unit per 25 ft. The boundary is `<=`: 100 ft is standard with no
 * overage, 101 ft is standard + 1 (brief §6.1).
 *
 * A run of zero is NOT a run. Every other quantity in this engine withholds at
 * zero, but the standard condensate line was hard-coded to 1 and so billed a
 * full standard route off `feet: 0` — which is exactly what an untouched
 * stepper reads, and what a surveyor who has not yet paced the route leaves
 * behind. Zero (or a non-finite value) now yields no line at all, and H3 stays
 * visibly unanswered for the gap detector rather than quietly priced.
 */
export function condensateUnits(feet: number): CondensateUnits {
  if (!Number.isFinite(feet) || feet <= 0) return { standard: 0, overage: 0 };
  const over = feet - CONDENSATE_STANDARD_FEET;
  return { standard: 1, overage: over > 0 ? ceilUnits(over, CONDUIT_UNIT_FEET) : 0 };
}

/**
 * The standard line for any measured run, plus an overage sub-line when the run
 * runs long. Two lines because the catalog prices them as two items (spec §3.4).
 */
export function condensateLines(feet: number, scope: Scope): DerivedLine[] {
  const units = condensateUnits(feet);
  const lines: DerivedLine[] = [];
  if (units.standard > 0) {
    lines.push(
      derivedLine({
        questionId: 'H3',
        scope,
        quantity: units.standard,
        uom: CONDENSATE_STANDARD_UOM,
        basis: `standard run — covers up to ${CONDENSATE_STANDARD_FEET} ft`,
      }),
    );
  }
  if (units.overage > 0) {
    lines.push(
      derivedLine({
        questionId: CONDENSATE_OVERAGE_ID,
        catalogId: 'H3',
        scope,
        quantity: units.overage,
        // formatFeet, not raw subtraction: `130.3 - 100` is 30.30000000000001,
        // and the chip printed every digit of it.
        basis:
          `${formatFeet(feet)} ft is ${formatFeet(feet - CONDENSATE_STANDARD_FEET)} ft past ` +
          `${CONDENSATE_STANDARD_FEET} ft — ÷ ${CONDUIT_UNIT_FEET}, rounded up`,
      }),
    );
  }
  return lines;
}

/* ═════════════════════════ lobbies, per group (§3.7) ═════════════════════ */

/** L1 — the landings come from the group definition (M6) and are never re-asked. */
export function landingsLine(landings: number, scope: Scope): DerivedLine {
  return derivedLine({
    questionId: 'L1',
    scope,
    quantity: landings,
    basis: 'taken from the group you set up',
  });
}

/**
 * L8 — a new riser is priced per landing of the group it serves, and one line
 * bundles conduit, back boxes, and cut & patch. Max one riser per group, which
 * the per-group scope guarantees structurally (Q12).
 */
export function riserLine(landings: number, scope: Scope): DerivedLine {
  return derivedLine({
    questionId: 'L8',
    scope,
    quantity: landings,
    basis: `one riser for this group, priced per landing — ${plural(landings, 'landing')}`,
  });
}

/* ══════════════════════ walking a whole survey document ══════════════════ */

/**
 * Every AUTO quantity in a survey, at the scope that owns it, in walk order:
 * the building's ATS chain first, then each room in document order (its own
 * room-level lines, then one set of lobby lines per group).
 *
 * There are no hoistway-scoped derived lines: nothing in specification §3.6
 * (Overhead) or §3.8 (Pit) is AUTO — they are all FLAT, INFO, or surveyor
 * counts. The hoistway loop is deliberately absent rather than accidentally
 * missing; if a future amendment adds one, it belongs right after the groups.
 */
export function computeDerived(doc: SurveyDocument): DerivedLine[] {
  const lines: DerivedLine[] = [];

  /** Drop zero-quantity lines: they are never money and only clutter the chip. */
  const emit = (line: DerivedLine): void => {
    if (line.quantity > 0) lines.push(line);
  };

  /* ── §3.1 building: the ATS chain, gated on B13 ─────────────────────── */
  const ats = atsRollup(doc.rooms);
  if (isQuestionVisible(doc, 'B14', {})) emit(atsCarCountLine(ats));
  if (isQuestionVisible(doc, 'B17', {})) emit(atsRoomCountLine(ats));

  /* ── §3.11 FLS back boxes: unconditional, building-wide (Amendment 5) ── */
  // No gate at all — F1 carries no `showWhen` — but it still goes through
  // isQuestionVisible so that a future gate lands in the catalog rather than
  // here. A survey with no groups yet counts zero landings and `emit` drops it,
  // which is the same rule every other line obeys.
  if (isQuestionVisible(doc, 'F1', {})) emit(flsBackBoxLine(doc.rooms));

  for (const room of doc.rooms) {
    const scope: Scope = { roomId: room.id };
    const cars = roomUnitCount(room);

    /* ── §3.2 the room unit count, derived from the car list (Q4) ─────── */
    emit(roomUnitCountLine(room));

    /* ── §3.3 electrical ──────────────────────────────────────────────── */
    // E4b / E4c — the shunt branch, open only when M4 (sprinkler) = Y.
    if (isQuestionVisible(doc, 'E4b', scope)) emit(externalShuntLine(scope));
    if (isQuestionVisible(doc, 'E4c', scope)) {
      emit(
        shuntedDisconnectLine(
          {
            cars,
            replaced: flagCountAnswer(doc, 'E1a', scope),
            refurbished: flagCountAnswer(doc, 'E1b', scope),
            refurbishedHaveShunts: boolAnswer(doc, 'E4c1', scope),
          },
          scope,
        ),
      );
    }

    // E6 / E6a — the Y/N is the surveyor's; both quantities are ours. E6a is
    // gated on its OWN catalog condition (E6 = Y) rather than nested inside the
    // E6 branch: nesting would make this code, not the catalog, the authority on
    // when a CAT6 run is added, and the two could then drift apart silently.
    if (isQuestionVisible(doc, 'E6', scope) && boolAnswer(doc, 'E6', scope) === true) {
      emit(emergencyCommsDisconnectLine(cars, scope));
    }
    if (isQuestionVisible(doc, 'E6a', scope)) emit(cat6Line(cars, scope));

    // E7b / E7c — conduit to the lobby panel, only when it cannot be reused.
    if (isQuestionVisible(doc, 'E7b', scope)) {
      const floors = numberAnswer(doc, 'E7b', scope);
      if (floors !== undefined) emit(conduitVerticalLine(floors, scope));
    }
    if (isQuestionVisible(doc, 'E7c', scope)) {
      const feet = feetAnswer(doc, 'E7c', scope);
      if (feet !== undefined) emit(conduitHorizontalLine(feet, scope));
    }

    // E8 — outlets, one per car, on the surveyor's Yes.
    if (isQuestionVisible(doc, 'E8', scope) && boolAnswer(doc, 'E8', scope) === true) {
      emit(outletLine(cars, scope));
    }

    // E9d — the fixtures needed (E9a), nothing added (D-045; E9b/E9c deleted
    // by D-046).
    if (isQuestionVisible(doc, 'E9d', scope)) {
      const needed = numberAnswer(doc, 'E9a', scope);
      if (needed !== undefined) {
        emit(ledFixtureLine(needed, scope));
      }
    }

    /* ── §3.4 HVAC: condensate, Mini-Split only ───────────────────────── */
    if (isQuestionVisible(doc, 'H3', scope)) {
      const feet = feetAnswer(doc, 'H3', scope);
      if (feet !== undefined) condensateLines(feet, scope).forEach(emit);
    }

    /* ── §3.7 lobbies, one set per group ──────────────────────────────── */
    for (const group of room.groups) {
      emitGroupLines(doc, room, group, emit);
    }
  }

  return lines;
}

/**
 * Takes the SAME `emit` the room walk uses rather than the raw array. These two
 * lines previously pushed straight onto `lines`, so the zero-quantity filter —
 * the rule that keeps a 0 × unit-price line out of an estimate — was enforced
 * for every other line in the engine and re-implemented by hand here as two
 * `group.landings > 0` guards. One filter, one place.
 */
function emitGroupLines(
  doc: SurveyDocument,
  room: MachineRoom,
  group: Group,
  emit: (line: DerivedLine) => void,
): void {
  const scope: Scope = { roomId: room.id, groupId: group.id };
  // Landings come from the group definition, which schema.ts already pins to a
  // positive integer; wholeCount is the same belt-and-braces the number answers
  // get, so a half landing can never reach a per-landing price.
  const landings = wholeCount(group.landings);

  // L1 — landings, straight off the group definition.
  if (isQuestionVisible(doc, 'L1', scope)) emit(landingsLine(landings, scope));

  // L8 — one riser per group at most, priced per landing.
  if (isQuestionVisible(doc, 'L8', scope) && boolAnswer(doc, 'L8', scope) === true) {
    emit(riserLine(landings, scope));
  }
}
